import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Login from './components/Login';
import LoginGuard from './components/LoginGuard';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Accounts from './components/Accounts';
import AccountDetail from './components/AccountDetail';
import Status from './components/Status';
import Storage from './components/Storage';
import StorageObject from './components/StorageObject';
import RPC from './components/RPC';
import ApiExplorer from './components/ApiExplorer';
import Leaderboards from './components/Leaderboards';
import LeaderboardDetail from './components/LeaderboardDetail';
import Groups from './components/Groups';
import GroupDetail from './components/GroupDetail';
import ChatMessages from './components/ChatMessages';
import ChatMessageDetail from './components/ChatMessageDetail';
import Notifications from './components/Notifications';
import NotificationDetail from './components/NotificationDetail';
import Users from './components/Users';
import Configuration from './components/Configuration';
import RuntimeModules from './components/RuntimeModules';
import Purchases from './components/Purchases';
import Subscriptions from './components/Subscriptions';
import Matches from './components/Matches';
import Analytics from './components/Analytics';
import AdManagement from './components/AdManagement';
import CoinManagement from './components/CoinManagement';
import Dashboard from './components/Dashboard';
import LudoGame from './components/Games/LudoGame';
import QuizGame from './components/Games/QuizGame';
import SolitaireGame from './components/Games/SolitaireGame';
import QuizManagement from './components/QuizManagement';
import ContainerSettings from './components/ContainerSettings';
import LudoSettings from './components/LudoSettings/LudoSettings';
import QuizSettings from './components/QuizSettings/QuizSettings';
import SolitaireSettings from './components/SolitaireSettings/SolitaireSettings';
import Events from './components/Events';
import SendNotification from './components/SendNotification';
import './App.css';

// Placeholder MFA component
const MFA: React.FC = () => {
  return (
    <div style={{ padding: '20px' }}>
      <h2>MFA Setup</h2>
      <p>MFA setup component would go here.</p>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
          <Routes>
            {/* Protected Dashboard Route */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Protected Accounts Route */}
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Accounts />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Account Detail Routes with nested tabs */}
            <Route
              path="/accounts/:userId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AccountDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/:userId/:tab"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AccountDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Status Route */}
            <Route
              path="/status"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Status />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Storage Routes */}
            <Route
              path="/storage"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Storage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/storage/:collection/:key/:userId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <StorageObject />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* RPC Route */}
            <Route
              path="/rpc"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RPC />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* API Explorer Route */}
            <Route
              path="/apiexplorer"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ApiExplorer />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Leaderboards Routes */}
            <Route
              path="/leaderboards"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Leaderboards />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboards/:leaderboardId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LeaderboardDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Groups Routes */}
            <Route
              path="/groups"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Groups />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/:groupId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GroupDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Chat Messages Routes */}
            <Route
              path="/chat-messages"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChatMessages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat-messages/:messageId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChatMessageDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Notifications Routes */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Notifications />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/:notificationId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NotificationDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* User Management Route */}
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Configuration Route */}
            <Route
              path="/config"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Configuration />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Runtime Modules Route */}
            <Route
              path="/modules"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RuntimeModules />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Purchases Route */}
            <Route
              path="/purchases"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Purchases />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Subscriptions Route */}
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Subscriptions />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Matches Route */}
            <Route
              path="/matches"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Matches />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Analytics Route */}
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Quiz Management Route */}
            <Route
              path="/quiz-management"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuizManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Ad Management Route */}
            <Route
              path="/ad-management"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Coin Management Route */}
            <Route
              path="/shop"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CoinManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />


            {/* Game Routes */}
            <Route
              path="/games/ludo"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LudoGame />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/games/quiz"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuizGame />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/games/solitaire"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SolitaireGame />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Game Settings Routes */}
            <Route
              path="/ludo-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LudoSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quiz-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuizSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/solitaire-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SolitaireSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Container Settings Route */}
            <Route
              path="/container-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContainerSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Events Route */}
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Events />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Send Notification Route */}
            <Route
              path="/send-notification"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SendNotification />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Login Routes */}
            <Route
              path="/login"
              element={
                <LoginGuard>
                  <Login />
                </LoginGuard>
              }
            >
              {/* Nested MFA route */}
              <Route path="mfa" element={<MFA />} />
            </Route>

            {/* Catch all route - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
